/**
 * The design document: the visual editor's authoring model.
 *
 * It is *not* a second render truth. The canonical artifact stays the Twig/HTML
 * `body` the backend and Mailcoach render (`EmailTemplate::renderEmail` →
 * Mailcoach `RenderTwigAction`); this model compiles one-way into it via
 * `compile()`, and travels back inside the body itself as a `{# cvn:design #}`
 * Twig comment (see `envelope.ts`).
 *
 * Plan: docs/plans/email-visual-editor.md
 */

import { hasBlockOverrides, splitLayoutOverrides } from './layoutOverrides';

/** Bumped only on breaking shape changes; `loadDocument` migrates forward. */
export const DOCUMENT_VERSION = 1;

/** Fallback `{% block %}` name when the document doesn't name a layout slot. */
export const DEFAULT_LAYOUT_SLOT = 'content';

/**
 * Block types the editor knows. Core components (`core.*`) are Twig files
 * seeded as global template fragments and land in Sprint 1; the primitives
 * below are the ones with TypeScript emitters.
 */
export const NATIVE_BLOCK = {
  /**
   * Raw Twig/HTML passed through untouched. Doubles as the container for
   * legacy bodies, which are never auto-converted into blocks.
   */
  HTML: 'native.html',
} as const;

/**
 * A colour is either a literal hex or bound to the journey theme. Theme
 * binding matters because `Configuration::getEndUserEmailVars` injects the
 * journey `theme` into every customer email — an editor that only wrote hex
 * would silently unbrand emails.
 */
export type ThemeableColor =
  | { literal: string }
  /** Resolved as `{{ theme.colors.<token> }}`. */
  | { token: string };

export interface DocumentSettings {
  contentWidth: number;
  pageBackground: ThemeableColor;
  contentBackground: ThemeableColor;
  fontStack: string;
  baseFontSize: number;
  linkColor: ThemeableColor;
  mobileBreakpoint: number;
  textDirection: 'ltr' | 'rtl';
}

/**
 * How a clause tests its variable.
 *
 * `set` / `unset` stand alone; every other operator compares against a value the
 * author types. The comparisons (`gt`…`lte`) read as numbers when the typed value
 * is one — see `compileClause`.
 */
export type ConditionOperator =
  | 'set'
  | 'unset'
  | 'eq'
  | 'neq'
  | 'contains'
  | 'ncontains'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte';

export interface ConditionClause {
  /** A send-time variable path from `GET /email-variables`, e.g. `event.branch`. */
  path: string;
  op: ConditionOperator;
  /** Compared literal for every operator except `set` / `unset`. */
  value?: string;
}

/** Compiles to an `{% if %}` wrapper around the block. */
export interface Condition {
  mode: 'all' | 'any';
  clauses: ConditionClause[];
}

export interface BlockSpacing {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

export interface BlockStyle {
  padding?: BlockSpacing;
  align?: 'left' | 'center' | 'right';
  background?: ThemeableColor;
  /** Overrides applied inside the compiler-owned mobile media query. */
  mobile?: {
    padding?: BlockSpacing;
    align?: 'left' | 'center' | 'right';
    fontSize?: number;
    hidden?: boolean;
  };
}

export interface Block {
  /** Stable across edits; emitted as `data-cvn-id` by core components. */
  id: string;
  /**
   * Which `{% block %}` of the template's layout this block fills — for
   * **top-level blocks only**.
   *
   * A slot-based layout like `layout/xxxl/base` declares nine named regions
   * (`header`, `intro_content`, `footer`, …) and a real template fills several. A
   * block with no region falls back to `EmailDocument.layoutSlot`, so documents
   * written before this existed compile exactly as they did.
   *
   * Nested blocks never carry one: they live inside a parent that is already in a
   * region, and honouring a second would be impossible.
   */
  layoutSlot?: string;
  /** `core.button` | `fragment:partials/xxxl/footer` | `native.html` */
  type: string;
  props: Record<string, unknown>;
  style?: BlockStyle;
  visibility?: Condition;
  /** One array per slot/column; slot order matches the component manifest. */
  children?: Block[][];
}

export interface EmailDocument {
  v: number;
  /** Mirrors `EmailTemplate.layout_key`; null means the core layout. */
  layoutKey: string | null;
  /** Which `{% block %}` of the layout the content fills. */
  layoutSlot?: string;
  /**
   * The blocks are a verbatim carrier for a body the editor did not author, so
   * the compiler must emit them **raw** — no `{% block %}` wrapper — even when
   * `layoutKey` is set.
   *
   * Set for legacy containment. Such a body is already whatever it needs to be:
   * a complete HTML document, or a set of `{% block %}` overrides for its
   * layout. Wrapping either in a further block is wrong, and in the second case
   * silently destroys the email — Twig discards content outside a block, and the
   * fabricated wrapper name usually isn't one the layout declares.
   */
  rawPassthrough?: boolean;
  /**
   * The body was edited **as code**, so it — not this document — is the authority.
   *
   * The document rides along as a snapshot so *Restore the visual version* remains
   * possible; block editing is off while it is set, because the document no longer
   * describes the body and pretending otherwise would be a lie the compiler would
   * have to break.
   *
   * The edited source is **not** stored here: it is the body itself. Putting it in
   * the document would nest the body inside a comment inside the body, doubling on
   * every edit.
   */
  detached?: boolean;
  settings: DocumentSettings;
  blocks: Block[];
}

/**
 * How faithfully a stored body could be turned back into a document.
 *
 * - `exact`   — a `{# cvn:design #}` envelope was decoded; round-trip closed.
 * - `high`    — structurally parsed from fragment includes (Sprint 7).
 * - `legacy`  — no envelope and not fragment-shaped: the whole body is held in
 *               a single `native.html` block. Never auto-converted.
 * - `detached` — an envelope was decoded, but the body has since been hand-edited,
 *               so the document is a stale snapshot of it.
 */
export type DocumentFidelity = 'exact' | 'high' | 'legacy' | 'detached';

export const DEFAULT_SETTINGS: DocumentSettings = {
  contentWidth: 600,
  pageBackground: { literal: '#F4F4F5' },
  contentBackground: { literal: '#FFFFFF' },
  fontStack: 'Helvetica, Arial, sans-serif',
  baseFontSize: 16,
  linkColor: { token: 'brand' },
  mobileBreakpoint: 600,
  textDirection: 'ltr',
};

let blockCounter = 0;

/**
 * Short, collision-resistant block id. Ids only need to be unique within one
 * document, and they end up in the compiled body as `data-cvn-id`, so they stay
 * short and URL-safe rather than being UUIDs.
 */
export function nextBlockId(): string {
  blockCounter += 1;
  const random = Math.random().toString(36).slice(2, 6);
  return `b${blockCounter.toString(36)}${random}`;
}

export function createHtmlBlock(html: string, id?: string): Block {
  return {
    id: id ?? nextBlockId(),
    type: NATIVE_BLOCK.HTML,
    props: { html },
  };
}

export function createEmptyDocument(
  overrides: Partial<EmailDocument> = {},
): EmailDocument {
  return {
    v: DOCUMENT_VERSION,
    layoutKey: null,
    settings: { ...DEFAULT_SETTINGS },
    blocks: [],
    ...overrides,
  };
}

/**
 * Wraps a whole existing body in one raw-HTML block. Used for legacy templates
 * (complete HTML documents with Twig inside HTML comments) which must stay
 * byte-identical until a user explicitly rebuilds them.
 */
export function createLegacyDocument(
  body: string,
  layoutKey: string | null = null,
): EmailDocument {
  return createEmptyDocument({
    layoutKey,
    // The layout is recorded so previews and preflight still see it, but the
    // body is emitted verbatim — see `rawPassthrough`.
    rawPassthrough: true,
    blocks: [createHtmlBlock(body)],
  });
}

/**
 * A body that is already a complete HTML document — an imported marketing email.
 * Mirrors `EmailTemplate::isCompleteHtmlDocument`: such a body is the whole email
 * and must never be treated as the content of someone else's layout.
 */
function isCompleteHtmlDocument(body: string): boolean {
  return /<!DOCTYPE\s|<html[\s>]/i.test(body);
}

/**
 * A hand-written body for a template that names a layout, opened as blocks.
 *
 * These are the templates the editor could not touch: no envelope, so they loaded
 * as one opaque `native.html` block with the structure locked — no canvas, no
 * palette, nothing but the Source view. But a layouted body is *already* divided
 * into regions by its own `{% block %}` overrides, and a region is exactly what
 * `Block.layoutSlot` is. Splitting on them gives every region its own block, so
 * the email opens on the canvas and content can be placed alongside what is there.
 *
 * Returns null when the body is not override-shaped, which keeps the verbatim
 * path for anything this cannot honestly describe.
 */
export function createLayoutedLegacyDocument(
  body: string,
  layoutKey: string,
): EmailDocument | null {
  const overrides = splitLayoutOverrides(body);

  if (overrides && overrides.length > 0) {
    return createEmptyDocument({
      layoutKey,
      blocks: overrides.map((override) => ({
        ...createHtmlBlock(override.content),
        layoutSlot: override.name,
      })),
    });
  }

  /**
   * A layouted body with no overrides at all renders as **nothing** today: the
   * renderer prepends `{% extends %}`, and Twig discards every byte outside a
   * block. Seeding the default region is what makes such a body render at all —
   * and if the layout names its region something else, `preflight` says so
   * instead of the send silently losing it.
   *
   * A complete HTML document is left alone: it is an email in its own right, not
   * the filling for someone else's layout.
   */
  if (
    overrides === null &&
    !hasBlockOverrides(body) &&
    !isCompleteHtmlDocument(body)
  ) {
    return createEmptyDocument({
      layoutKey,
      blocks: [{ ...createHtmlBlock(body), layoutSlot: DEFAULT_LAYOUT_SLOT }],
    });
  }

  return null;
}

/**
 * A contained document re-opened as blocks.
 *
 * Containment used to be permanent: merely opening a legacy template and saving
 * wrote an envelope carrying `rawPassthrough`, and from then on the body would
 * decode straight back into the locked blob — so the split above would never get
 * a chance at it. The envelope's own settings are kept; everything else about the
 * document was the containment.
 *
 * Not for a detached document: there the *body* is the authority and this
 * document is only a snapshot of it, so re-reading the snapshot's blocks would
 * describe something the user is no longer editing.
 */
export function upgradeContainedDocument(
  document: EmailDocument,
  layoutKey: string,
): EmailDocument | null {
  if (!document.rawPassthrough || document.detached) return null;
  if (document.blocks.length !== 1) return null;

  const [contained] = document.blocks;
  if (contained.type !== NATIVE_BLOCK.HTML) return null;
  if (typeof contained.props.html !== 'string') return null;

  const split = createLayoutedLegacyDocument(contained.props.html, layoutKey);
  return split && { ...split, settings: document.settings };
}

/** Depth-first walk over the document's blocks, including nested slots. */
export function forEachBlock(
  blocks: Block[],
  visit: (block: Block, parents: Block[]) => void,
  parents: Block[] = [],
): void {
  for (const block of blocks) {
    visit(block, parents);
    for (const slot of block.children ?? []) {
      forEachBlock(slot, visit, [...parents, block]);
    }
  }
}
